import { type AllegatoRichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/allegatoRichiestaDTO.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaModuloDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTO.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import {
  createColumnFilter,
  FilterInputType,
} from '@framework/ui/components/table/TableColumnFilter.tsx';
import { type ColumnType } from 'antd/es/table';
import { AttachmentActionsCell } from './AttachmentActionsCell.tsx';
import { OmissisSubRowLabel } from './OmissisSubRowLabel.tsx';

export interface IAttachmentTableRow {
  key: string;
  attachment: AllegatoRichiestaDTO;
  typeName: string;
  fileName: string;
  description: string;
  moduleName: string;
  isOmissis: boolean;
}

export function getModuleCode(
  modules: RichiestaModuloDTO[],
  moduleId: string | null | undefined,
): string {
  if (!moduleId) return '';
  const match = modules.find(m => m.id === moduleId);
  return match?.modulo?.modulo?.codice ?? '';
}

export function buildAttachmentRow(
  attachment: AllegatoRichiestaDTO,
  isOmissis: boolean,
  modules: RichiestaModuloDTO[],
): IAttachmentTableRow {
  return {
    key: isOmissis ? `${attachment.id}-omissis` : (attachment.id ?? ''),
    attachment,
    typeName: (attachment.tipoAllegato as string) ?? '',
    fileName: isOmissis
      ? (attachment.nomeFileOmissis ?? '')
      : (attachment.nomeFile ?? ''),
    description: attachment.descrizioneInterna ?? '',
    moduleName: getModuleCode(modules, attachment.idRichiestaModulo),
    isOmissis,
  };
}

export const attachmentsColumnsBase = {
  typeName: {
    title: t('submission-summary.tipologia', 'Tipologia'),
    key: 'typeName',
    dataIndex: 'typeName',
    ...createColumnFilter<IAttachmentTableRow>(FilterInputType.Text),
    onFilter: (value, record) =>
      record.typeName.toLowerCase().includes(String(value).toLowerCase()),
    render: (typeName: string, record: IAttachmentTableRow) =>
      record.isOmissis ? null : typeName,
  },
  fileName: {
    title: t('submission-summary.nome-file', 'Nome del file'),
    key: 'fileName',
    dataIndex: 'fileName',
    ...createColumnFilter<IAttachmentTableRow>(FilterInputType.Text),
    onFilter: (value, record) =>
      record.fileName.toLowerCase().includes(String(value).toLowerCase()),
    render: (fileName: string, record: IAttachmentTableRow) =>
      record.isOmissis ? <OmissisSubRowLabel /> : fileName || '-',
  },
  description: {
    title: t('submission-summary.descrizione', 'Descrizione'),
    key: 'description',
    dataIndex: 'description',
    ...createColumnFilter<IAttachmentTableRow>(FilterInputType.Text),
    onFilter: (value, record) =>
      record.description.toLowerCase().includes(String(value).toLowerCase()),
    render: (description: string, record: IAttachmentTableRow) =>
      record.isOmissis ? null : description,
  },
  moduleName: {
    title: t('submission-summary.nome-modulo', 'Modulo'),
    key: 'moduleName',
    dataIndex: 'moduleName',
    ...createColumnFilter<IAttachmentTableRow>(FilterInputType.Text),
    onFilter: (value, record) =>
      record.moduleName.toLowerCase().includes(String(value).toLowerCase()),
    render: (moduleName: string, record: IAttachmentTableRow) =>
      record.isOmissis ? null : moduleName,
  },
} satisfies Record<string, ColumnType<IAttachmentTableRow>>;

/**
 * Restituisce tutte le colonne per la tabella allegati,
 * inclusa la colonna azioni.
 */
export function getAttachmentsColumns(
  submissionId: string,
  modules: RichiestaModuloDTO[],
  submission?: RichiestaDTO,
): ColumnType<IAttachmentTableRow>[] {
  return [
    attachmentsColumnsBase.typeName,
    attachmentsColumnsBase.fileName,
    attachmentsColumnsBase.description,
    attachmentsColumnsBase.moduleName,
    {
      title: t('submission-summary.azioni', 'Azioni'),
      key: 'actions',
      width: 120,
      align: 'left' as const,
      render: (_, record) => (
        <AttachmentActionsCell
          submissionId={submissionId}
          record={record}
          modules={modules}
          submission={submission}
        />
      ),
    },
  ];
}
